import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Tag, ArrowsDownUp } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Nomenklatur() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", sort_order: "" });
  const canManage = ["superadmin","admin","finance"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/nomenklatur");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    const nextOrder = (items.reduce((max, n) => Math.max(max, n.sort_order || 0), 0) + 10);
    setForm({ code: "", name: "", sort_order: nextOrder });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ code: it.code, name: it.name, sort_order: it.sort_order ?? "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, sort_order: form.sort_order === "" ? null : Number(form.sort_order) };
      if (editing) await api.put(`/nomenklatur/${editing.code}`, payload);
      else await api.post("/nomenklatur", payload);
      toast.success("Nomenklatur tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (code) => {
    if (!confirm(`Hapus nomenklatur "${code}"?`)) return;
    try { await api.delete(`/nomenklatur/${code}`); toast.success("Terhapus"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const quickReorder = async (nomen, delta) => {
    const currentOrder = nomen.sort_order ?? 999;
    try {
      await api.put(`/nomenklatur/${nomen.code}`, { code: nomen.code, name: nomen.name, sort_order: currentOrder + delta });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="nomenklatur-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Master Data</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Nomenklatur Keuangan</h1>
          <p className="text-gray-600 text-sm mt-1">Kelola kode, nama, dan <b>urutan tampil</b> pos keuangan. Urutan ini dipakai di laporan Rekap Tagihan.</p>
        </div>
        {canManage && <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="nomen-add-button"><Plus size={16} className="mr-1.5" />Tambah Nomenklatur</Button>}
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 font-semibold text-center w-20">Urutan</th>
                <th className="px-4 py-3 font-semibold">Kode</th>
                <th className="px-4 py-3 font-semibold">Nama Nomenklatur</th>
                <th className="px-4 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.code} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-center">
                    <div className="inline-flex items-center gap-1">
                      <span className="font-mono font-black text-lg text-gray-700 w-8 text-center">{n.sort_order ?? "—"}</span>
                      {canManage && (
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => quickReorder(n, -10)} title="Naikkan (kurangi 10)" className="text-xs text-gray-400 hover:text-[#0B8D95]">▲</button>
                          <button onClick={() => quickReorder(n, 10)} title="Turunkan (tambah 10)" className="text-xs text-gray-400 hover:text-[#0B8D95]">▼</button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-md bg-[#0B8D95]/10 text-[#0B8D95] flex items-center justify-center"><Tag size={16} weight="duotone" /></div>
                      <span className="font-mono font-bold text-[#0B8D95]">{n.code}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-900">{n.name}</td>
                  <td className="px-4 py-3 text-right">
                    {canManage && <>
                      <button onClick={()=>openEdit(n)} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`nomen-edit-${n.code}`}><PencilSimple size={14} /></button>
                      <button onClick={()=>remove(n.code)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`nomen-delete-${n.code}`}><Trash size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-gray-500">Belum ada nomenklatur</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="p-3 bg-blue-50 border-t border-blue-200 text-xs text-blue-900 flex items-center gap-2">
          <ArrowsDownUp size={16} />
          <span><b>Tips urutan:</b> gunakan kelipatan 10 (10, 20, 30, ...) — biar mudah menyisipkan pos baru di tengah tanpa perlu re-order semua.</span>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="nomen-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Nomenklatur" : "Tambah Nomenklatur"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kode * <span className="text-xs text-gray-500">(contoh: AJP, IPP, PSB)</span></Label>
              <Input value={form.code} onChange={(e)=>setForm({...form, code: e.target.value.toUpperCase()})} disabled={!!editing} className="font-mono uppercase" data-testid="nomen-code-input" /></div>
            <div><Label>Nama *</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="nomen-name-input" /></div>
            <div><Label>Urutan Tampil <span className="text-xs text-gray-500">(angka kecil tampil dulu di Rekap)</span></Label>
              <Input type="number" value={form.sort_order} onChange={(e)=>setForm({...form, sort_order: e.target.value})} placeholder="10, 20, 30, ..." data-testid="nomen-order-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="nomen-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
